import { useTranslation } from 'react-i18next';
import type { SaveState } from '../../hooks/use-quote-editor.ts';
import { Spinner } from '../ui.tsx';
import { getDateLocale } from '../../i18n/locale.ts';

const MESSAGE_KEYS: Record<SaveState, { key: string; tone: string }> = {
  idle: { key: 'saveIndicator.allSaved', tone: 'text-[color:var(--color-muted)]' },
  pending: { key: 'saveIndicator.unsaved', tone: 'text-[color:var(--color-muted)]' },
  saving: { key: 'saveIndicator.saving', tone: 'text-[color:var(--color-muted)]' },
  saved: { key: 'saveIndicator.allSaved', tone: 'text-[color:var(--color-brand)]' },
  offline: { key: 'saveIndicator.offline', tone: 'text-[color:var(--color-warning)]' },
  error: { key: 'saveIndicator.error', tone: 'text-[color:var(--color-danger)]' },
  conflict: { key: 'saveIndicator.conflict', tone: 'text-[color:var(--color-danger)]' },
};

/**
 * Autosave status. `aria-live="polite"` announces each change to screen-reader
 * users without stealing focus, so "saving" and "saved" are not purely visual.
 */
export function SaveIndicator({ state, lastSavedAt }: { state: SaveState; lastSavedAt: Date | null }) {
  const { t } = useTranslation();
  const message = MESSAGE_KEYS[state];
  const time =
    state === 'saved' && lastSavedAt
      ? t('saveIndicator.savedAt', {
          time: new Intl.DateTimeFormat(getDateLocale(), { hour: 'numeric', minute: '2-digit' }).format(lastSavedAt),
        })
      : '';

  return (
    <p className={`flex items-center gap-2 text-sm font-medium ${message.tone}`} aria-live="polite" role="status">
      {state === 'saving' ? <Spinner /> : null}
      {t(message.key)}
      {time}
    </p>
  );
}
